import Modal from '@mui/material/Modal'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React, { ReactNode, useEffect, useState } from 'react'

type LoaderProps = {
  /** Pass a translated description of the actual operation when available. */
  label?: string
}

function Loader({ label }: LoaderProps = {}) {
  const { t } = useTranslation('common')
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 8000)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="spotme-loader flex size-full min-h-32 items-center justify-center"
    >
      <span className="sr-only">
        {slow ? t('LOADING.SLOW') : (label ?? t('LOADING.DOTS'))}
      </span>
      <div className="relative flex flex-col items-center" aria-hidden="true">
        <div className="relative size-12">
          <div className="spotme-loader-shadow absolute -bottom-2 left-1/2 h-1 w-4 -translate-x-1/2 rounded-full bg-ink-700" />
          <div className="spotme-loader-mark">
            <Image
              src="/logos/logo-icon-blue.png"
              alt="Loading"
              width={48}
              height={48}
              priority
              className="relative z-10"
            />
          </div>
        </div>
        <p className="spotme-loader-caption absolute top-full mt-7 w-64 max-w-[calc(100vw-48px)] text-center text-sm leading-relaxed text-ink-200">
          {slow ? t('LOADING.SLOW') : (label ?? t('LOADING.DOTS'))}
        </p>
      </div>
    </div>
  )
}

function FullScreenLoader({ label }: LoaderProps = {}) {
  return (
    <div className="spotme-loader-overlay fixed inset-0 z-50 bg-white-500">
      <Loader label={label} />
    </div>
  )
}

/** Blocks interaction without unmounting the page or its providers. */
function LoadingOverlay({ label }: LoaderProps = {}) {
  return (
    <Modal open hideBackdrop className="spotme-loader-overlay z-[100001]">
      <div className="flex size-full items-center justify-center bg-white-500/90 outline-none">
        <Loader label={label} />
      </div>
    </Modal>
  )
}

function LoaderContainer({
  loading,
  children,
  fullScreen
}: {
  loading: boolean
  children?: ReactNode
  fullScreen?: boolean
}) {
  if (loading) {
    return fullScreen ? <FullScreenLoader /> : <Loader />
  }

  return <>{children}</>
}

const MemoContainer = React.memo(LoaderContainer)

export {
  FullScreenLoader,
  Loader,
  LoaderContainer,
  LoadingOverlay,
  MemoContainer
}
